import i18n from '@/utils/i18n';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  AllowedShortcuts,
  KeyboardScope,
} from '@/platform/shortcuts/operator-shortcuts';
import {
  APP_CONTAINER_ROUTES,
  APP_MODAL_ROUTES,
  LOCATION_MANAGER_ROUTES,
} from '@/navigators/routes';
import {
  AppContainerStackParams,
  AppModalStackParams,
} from '@/navigators/types';
import { BaseActionSheet } from '@/components/base-action-sheet';
import { TextInput as BaseTextInput } from 'react-native/Libraries/Components/TextInput/TextInput';
import { ChatUnreadCountMap } from '@/screens/chats/hooks/use-chat-unread-count';
import {
  CompositeNavigationProp,
  useNavigation,
} from '@react-navigation/native';
import { createStyles } from '@/contexts/theme-context';
import {
  DRAWER_HEADER_HEIGHT,
  HEADER_PADDING_HORIZONTAL_SMALL,
  SCREEN_PADDING_SMALL,
} from '@/styles';
import { FilterPills } from '@/components/filter-pills';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import {
  handleWebKeyPress,
  Key,
} from '@platform/shortcuts/utils/keyboard-platform';
import { LocationOption, LocationPickerList } from './location-picker-list';
import { MultiSelectFilterType } from '@/hooks/use-location-picker-filter-pills';
import { MultiStateCheckbox } from '@/components/multi-state-checkbox';
import { OfflineDownloadHeader } from './offline-download-header';
import { OfflineManagementActions } from './offline-management-actions';
import { OfflineManagementButton } from './offline-management-button';
import { PillButton, PillButtonType } from '@/components/pill-button';
import { Platform, useWindowDimensions, View } from 'react-native';
import { RecyclerListRef } from '@/components/recycler-list';
import { SearchFiltersActionSheet } from '@/components/search-filters/search-filters-action-sheet';
import { SearchInput } from '@/components/search-input';
import { StackNavigationProp } from '@react-navigation/stack';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useKeyboardShortcuts } from '@platform/shortcuts/hooks/use-keyboard-shortcuts';
import { useLayoutContext } from '@/contexts/layout-context';
import { useLocationPickerData } from '@/hooks/use-location-picker-data';
import { useMountedLayoutEffect } from '@/hooks/use-mounted-layout-effect';
import {
  useOfflineDownloadManagementContext,
  useSelectedOfflineRestaurantUUIDs,
} from '@/contexts/offline-download-management-context';
import { useOfflineModeEnabled } from '@/hooks/use-offline-mode-enabled';

const ACTION_SHEET_SIZE = {
  height: 780,
  width: 440,
};

type Navigation = CompositeNavigationProp<
  StackNavigationProp<AppContainerStackParams, APP_CONTAINER_ROUTES.Base>,
  StackNavigationProp<AppModalStackParams, APP_MODAL_ROUTES.LocationManager>
>;

interface Props {
  chatUnreadCountMap: ChatUnreadCountMap;
  onClose: () => void;
  restaurantsWithNewInvoices: Set<string>;
  targetRef: React.RefObject<View | null>;
}

export const LocationPickerActionSheet: React.FC<Props> = ({
  chatUnreadCountMap,
  onClose,
  restaurantsWithNewInvoices,
  targetRef,
}) => {
  const { isSmallLayout } = useLayoutContext();
  const [styles] = useStyles();
  const { height: windowHeight } = useWindowDimensions();
  const { restaurantRelationships } = useAuthenticatedUserContext();

  // The popover opens below the drawer header; clamp its height to the
  // remaining viewport space so no rows render off screen.
  const availableHeight =
    windowHeight - DRAWER_HEADER_HEIGHT - SCREEN_PADDING_SMALL;
  const popoverMaxHeight =
    availableHeight > 0
      ? Math.min(ACTION_SHEET_SIZE.height, availableHeight)
      : ACTION_SHEET_SIZE.height;
  const {
    accountModeFilter,
    dataStatus,
    filteredRestaurantRelationships,
    getPillRef,
    hasMultipleSuppliers,
    multiSelectFilters,
    onDisplayTextChange,
    pillButtonProps,
    refetchDataStatus,
    searching,
    searchText,
    syncedRestaurantUUIDs,
  } = useLocationPickerData(restaurantRelationships);

  const {
    cancelDownload,
    downloadProgress,
    resetDownloadProgress,
    resetSelection,
    selectAllRestaurants,
    toggleRestaurantSelection,
  } = useOfflineDownloadManagementContext((s) => ({
    cancelDownload: s.cancelDownload,
    downloadProgress: s.downloadProgress,
    resetDownloadProgress: s.resetDownloadProgress,
    resetSelection: s.resetSelection,
    selectAllRestaurants: s.selectAllRestaurants,
    toggleRestaurantSelection: s.toggleRestaurantSelection,
  }));
  const selectedOfflineRestaurantUUIDs = useSelectedOfflineRestaurantUUIDs();

  const isOfflineModeEnabled = useOfflineModeEnabled();
  const navigation = useNavigation<Navigation>();
  const inputRef = useRef<BaseTextInput>(null);
  const listRef = useRef<RecyclerListRef<LocationOption>>(null);

  // Hides the action sheet when the create / edit account screens are open
  // to prevent it from rendering over the screens
  const [showingScreen, setShowingScreen] = useState(false);
  const [isManagingOfflineAccounts, setIsManagingOfflineAccounts] =
    useState(false);

  useEffect(() => {
    if (Platform.OS === 'web' && inputRef.current) {
      inputRef.current?.focus();
    }
  }, []);

  useEffect(() => {
    if (!isManagingOfflineAccounts) {
      resetSelection();
    }
  }, [isManagingOfflineAccounts, resetSelection]);

  useKeyboardShortcuts(
    AllowedShortcuts.Search,
    () => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    },
    [],
    {},
    true,
    KeyboardScope.LOCATION_PICKER,
  );

  useMountedLayoutEffect(() => {
    onClose();
    // Closes the action sheet when the layout size changes
  }, [isSmallLayout]);

  const renderPillButton = useCallback((button: PillButtonType) => {
    return <PillButton button={button} key={button.label} ref={button.ref} />;
  }, []);

  const isPartiallySelected = useMemo(() => {
    const filteredRestaurantUUIDs = new Set(
      filteredRestaurantRelationships.map((rr) => rr.uuid),
    );
    const intersection = new Set(
      [...filteredRestaurantUUIDs].filter((x) =>
        selectedOfflineRestaurantUUIDs.has(x),
      ),
    );
    return (
      intersection.size > 0 &&
      intersection.size !== filteredRestaurantUUIDs.size
    );
  }, [filteredRestaurantRelationships, selectedOfflineRestaurantUUIDs]);

  return (
    <BaseActionSheet
      contentStyle={
        isSmallLayout
          ? styles.actionSheet
          : [styles.actionSheet, { maxHeight: popoverMaxHeight }]
      }
      isVisible={!showingScreen}
      onClose={onClose}
      popoverTarget={targetRef?.current}
    >
      <View style={styles.topContainer}>
        <View style={styles.row}>
          <Typography variant="heading-md">{i18n.t('accounts')}</Typography>
          {isOfflineModeEnabled ? (
            <OfflineManagementButton
              isDownloading={Boolean(downloadProgress)}
              isManagingOfflineAccounts={isManagingOfflineAccounts}
              setIsManagingOfflineAccounts={setIsManagingOfflineAccounts}
            />
          ) : null}
        </View>
        <SearchInput
          autoCorrect={false}
          containerStyle={styles.searchInput}
          inputRef={inputRef}
          onChangeText={onDisplayTextChange}
          onKeyPress={(e) => {
            handleWebKeyPress(e);

            switch (e.nativeEvent.key) {
              case Key.Escape:
                inputRef.current?.blur();
                break;
            }
          }}
          placeholder={i18n.t('search')}
          testID="location-picker-input"
          value={searchText}
        />
      </View>
      {filteredRestaurantRelationships.length > 0 ? (
        <View style={styles.filterPills}>
          {multiSelectFilters.map((filter) => (
            <SearchFiltersActionSheet<MultiSelectFilterType>
              buttonRef={getPillRef(filter.key)}
              filter={filter}
              isVisible={filter.isSelecting}
              key={filter.key}
              onClose={filter.onClose}
              onSelect={(_key, selectedValues) => {
                filter.setSelected(selectedValues);
                filter.onClose();
              }}
              trackingPrefix="accounts_selector"
            />
          ))}
          <FilterPills
            buttons={pillButtonProps}
            renderPillButton={renderPillButton}
          />
        </View>
      ) : null}
      {downloadProgress ? (
        <OfflineDownloadHeader
          cancelDownload={cancelDownload}
          resetDownloadProgress={resetDownloadProgress}
        />
      ) : isManagingOfflineAccounts ? (
        <View style={styles.selectAllContainer}>
          <Typography variant="heading-sm">{i18n.t('select_all')}</Typography>
          <MultiStateCheckbox
            isPartiallySelected={isPartiallySelected}
            isSelected={selectedOfflineRestaurantUUIDs.size > 0}
            onSelect={() =>
              selectAllRestaurants(filteredRestaurantRelationships)
            }
            trackingData={null}
          />
        </View>
      ) : null}
      <GestureHandlerRootView style={styles.gestureHandler}>
        <LocationPickerList
          accountModeFilter={accountModeFilter}
          bottomActions={[]}
          chatUnreadCountMap={chatUnreadCountMap}
          dataStatus={dataStatus}
          hasMultipleSuppliers={hasMultipleSuppliers}
          isMultiSelecting={isManagingOfflineAccounts}
          listRef={listRef}
          onCreateAccount={() => {
            setShowingScreen(true);

            navigation.navigate(APP_MODAL_ROUTES.LocationManager, {
              params: {
                initialName: searchText,
                onClose: () => setShowingScreen(false),
              },
              screen: LOCATION_MANAGER_ROUTES.CreateAccount,
            });
          }}
          onRestaurantSelected={onClose}
          onViewAccount={(option) => {
            setShowingScreen(true);

            navigation.navigate(APP_MODAL_ROUTES.LocationManager, {
              params: {
                onClose: () => setShowingScreen(false),
                onRemoveRestaurant: option.metadata?.onRemoveRestaurant,
                relationship: option.metadata?.relationship,
                restaurantName: option.title,
                restaurantUUID: String(option.value),
                supplierUUID: option.metadata?.relationship.supplierUUID,
              },
              screen: LOCATION_MANAGER_ROUTES.ViewAccount,
            });
          }}
          restaurantRelationships={filteredRestaurantRelationships}
          restaurantsWithNewInvoices={restaurantsWithNewInvoices}
          searching={searching}
          selectAccount={toggleRestaurantSelection}
          selectedAccountUUIDs={selectedOfflineRestaurantUUIDs}
        />
        {isManagingOfflineAccounts &&
        selectedOfflineRestaurantUUIDs.size > 0 &&
        !downloadProgress ? (
          <OfflineManagementActions
            onAction={() => setIsManagingOfflineAccounts(false)}
            syncedRestaurantUUIDs={syncedRestaurantUUIDs}
            updateDataStatus={refetchDataStatus}
          />
        ) : null}
      </GestureHandlerRootView>
    </BaseActionSheet>
  );
};

const useStyles = createStyles((theme) => ({
  actionSheet: {
    height: ACTION_SHEET_SIZE.height,
    paddingHorizontal: 0,
    width: ACTION_SHEET_SIZE.width,
  },
  filterPills: {
    paddingTop: 4,
  },
  gestureHandler: {
    flex: 1,
  },
  row: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingBottom: 8,
  },
  searchInput: {
    backgroundColor: theme.colors.background,
    height: 40,
  },
  selectAllContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: 24,
    paddingVertical: 14,
  },
  topContainer: {
    paddingHorizontal: HEADER_PADDING_HORIZONTAL_SMALL,
    paddingTop: HEADER_PADDING_HORIZONTAL_SMALL,
  },
}));
